// @subframe/sync-disable
"use client";
/*
 * Documentation:
 * Text Field — https://app.subframe.com/06b570ce924b/library?component=Text+Field_be48ca43-f8e7-4c0e-8870-d219ea11abfe
 */

import React from "react";
import * as SubframeCore from "@subframe/core";
import * as SubframeUtils from "../utils";

interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "placeholder" | "size"
> {
  placeholder?: React.ReactNode;
  size?: "base" | "large" | "x-large";
  variant?: "option-1" | "option-2";
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    placeholder,
    size = "base",
    variant = "option-1",
    className,
    ...otherProps
  }: InputProps,
  ref,
) {
  return (
    <input
      className={SubframeUtils.twClassNames(
        "group/b0d608f7 h-full w-full border-none bg-transparent px-0 py-0 text-body font-body text-neutral-900 outline-none placeholder:text-neutral-400",
        {
          "text-extra-large-input font-extra-large-input mobile:text-extra-large-input mobile:font-extra-large-input":
            size === "x-large",
          "text-large-button font-large-button mobile:text-large-button mobile:font-large-button":
            size === "large",
        },
        className,
      )}
      placeholder={placeholder as string}
      ref={ref}
      {...otherProps}
    />
  );
});

interface TextFieldRootProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  disabled?: boolean;
  error?: boolean;
  variant?: "outline" | "filled";
  size?: "base" | "large" | "x-large";
  label?: React.ReactNode;
  helpText?: React.ReactNode;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const TextFieldRoot = React.forwardRef<HTMLLabelElement, TextFieldRootProps>(
  function TextFieldRoot(
    {
      disabled = false,
      error = false,
      variant = "outline",
      size = "base",
      label,
      helpText,
      icon = null,
      iconRight = null,
      children,
      className,
      ...otherProps
    }: TextFieldRootProps,
    ref,
  ) {
    return (
      <label
        className={SubframeUtils.twClassNames(
          "group/be48ca43 flex flex-col items-start gap-1.5",
          className,
        )}
        ref={ref}
        {...otherProps}
      >
        {label ? (
          <span className="text-caption-bold font-caption-bold text-default-font">
            {label}
          </span>
        ) : null}
        <div
          className={SubframeUtils.twClassNames(
            "flex h-8 w-full flex-none items-center gap-1 rounded-md border border-solid border-secondary-beige-200 bg-default-background px-2 group-focus-within/be48ca43:border group-focus-within/be48ca43:border-solid group-focus-within/be48ca43:border-primary-rose",
            {
              "h-14 w-full flex-none rounded-full mobile:h-14 mobile:w-full mobile:flex-none mobile:rounded-full":
                size === "x-large",
              "h-10 w-full flex-none rounded-lg": size === "large",
              "border border-solid border-neutral-100 bg-neutral-100 group-hover/be48ca43:border group-hover/be48ca43:border-solid group-hover/be48ca43:border-secondary-beige-200 group-focus-within/be48ca43:bg-default-background":
                variant === "filled",
              "border border-solid border-error-600": error,
              "border border-solid border-neutral-200 bg-neutral-200": disabled,
            },
          )}
        >
          {icon ? (
            <SubframeCore.IconWrapper className="text-body font-body text-neutral-400">
              {icon}
            </SubframeCore.IconWrapper>
          ) : null}
          {children ? (
            <div className="flex grow shrink-0 basis-0 flex-col items-start self-stretch px-1">
              {children}
            </div>
          ) : null}
          {iconRight ? (
            <SubframeCore.IconWrapper
              className={SubframeUtils.twClassNames(
                "[&>*]:flex text-heading-3 font-heading-3 text-subtext-color pr-1",
                { "text-error-500": error },
              )}
            >
              {iconRight}
            </SubframeCore.IconWrapper>
          ) : null}
        </div>
        {helpText ? (
          <span
            className={SubframeUtils.twClassNames(
              "text-caption font-caption text-subtext-color",
              { "text-error-700": error },
            )}
          >
            {helpText}
          </span>
        ) : null}
      </label>
    );
  },
);

export const TextField = Object.assign(TextFieldRoot, {
  Input,
});
